import React from 'react';
import {CourseLearnersAnalyticsItem} from '../../../../../data/models/Gradebook/CourseAnalyticsModels';
import {analyticsText} from '../../../analyticsText';
import {getFirstCapitals} from '../../../../../utils/getFirstCapitals';
import Tooltip from '../../../../../uiToolkit/Tooltip';
import Chart from 'react-apexcharts';
import {ApexOptions} from 'apexcharts';
import {truncateNumberTo2Decimals} from '../../../../../utils/getTimeTextFromSeconds';
import {formatDateTime} from '../../../../../utils/notificationTimeTextConvertor';

interface Props {
  learners: CourseLearnersAnalyticsItem[];
  passingScore: number;
  onSelectLearner: (learnerId: string) => void;
  isGradable: boolean;
  onChangeSort: (field: string, asc: boolean) => void;
  sortField: string;
  asc: boolean;
}

const progressOptions = (status: number): ApexOptions => ({
  legend: {
    show: false,
  },
  states: {
    hover: {
      filter: {
        type: 'none',
      },
    },
  },
  chart: {
    height: '100%',
  },
  dataLabels: {
    enabled: false,
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
      donut: {
        size: '150%',
        labels: {
          total: {
            show: false,
          },
          name: {
            show: false,
          },
          value: {
            show: false,
          },
        },
      },
    },
  },
  fill: {
    colors: [
      '#E5E7EA',
      status === 0 ? '#53B483' : status === 1 ? '#3B82F6' : '#E5E7EA',
    ],
  },
  labels: ['', '', ''],
});

const LearnersAnalyticsTable = React.memo(
  ({
    learners,
    passingScore,
    onSelectLearner,
    isGradable,
    onChangeSort,
    sortField,
    asc,
  }: Props) => {
    const getSortingArrow = (field: string) => {
      if (sortField === field) {
        if (asc) {
          return <div className="arrow-down"></div>;
        } else {
          return <div className="arrow-up"></div>;
        }
      }
      return <div />;
    };

    return (
      <div className="CourseLearnersAnalyticsTableContainer">
        <table className="CourseLearnersAnalyticsTable">
          <thead>
            <tr>
              <th className="LearnerColumn">
                <div
                  className="SortArrowContainer"
                  style={{justifyContent: 'flex-start'}}
                  onClick={() => onChangeSort('Name', !asc)}>
                  {analyticsText.learner} {getSortingArrow('Name')}
                </div>
              </th>
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Due date', !asc)}>
                  {analyticsText.dueDate}
                  {getSortingArrow('Due date')}
                </div>
              </th>
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Status', !asc)}>
                  {analyticsText.status}
                  {getSortingArrow('Status')}
                </div>
              </th>
              {isGradable && (
                <th>
                  <div
                    className="SortArrowContainer"
                    onClick={() => onChangeSort('AvgScore', !asc)}>
                    {analyticsText.score} {getSortingArrow('AvgScore')}
                  </div>
                </th>
              )}
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Progress', !asc)}>
                  {analyticsText.progress} {getSortingArrow('Progress')}
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {learners.map(learner => (
              <tr key={learner.id}>
                <td className="LearnerColumn">
                  <div className="LearnerNameContainer">
                    {learner.avatarUrl ? (
                      <img src={learner.avatarUrl} />
                    ) : (
                      <div className="AvatarPlaceholder">
                        {getFirstCapitals(learner.name)}
                      </div>
                    )}
                    <Tooltip tooltipText={learner.name}>
                      <span>{learner.name}</span>
                    </Tooltip>
                  </div>
                </td>
                <td>
                  {learner.dueDate ? (
                    <span>{formatDateTime(learner.dueDate)}</span>
                  ) : (
                    <span>{analyticsText.noDueDate}</span>
                  )}
                </td>
                <td>
                  {learner.status === 0 ? (
                    <span className="StatusCompleted">
                      {analyticsText.completed}
                    </span>
                  ) : learner.status === 1 ? (
                    <span className="StatusProgress">
                      {analyticsText.inProgress}
                    </span>
                  ) : (
                    <span className="StatusNotStarted">
                      {analyticsText.notStarted}
                    </span>
                  )}
                </td>
                {isGradable && (
                  <td
                    className={
                      learner.avgScore == null
                        ? 'ScorePending'
                        : (learner.avgScore || 0) >= passingScore
                        ? 'ScoreSuccess'
                        : 'ScoreFail'
                    }>
                    {learner.avgScore != null
                      ? truncateNumberTo2Decimals(learner.avgScore) + '%'
                      : 'Pending'}
                  </td>
                )}
                <td>
                  <div className="ProgressChartContainer">
                    <Chart
                      options={progressOptions(learner.status)}
                      series={[100 - learner.progress, learner.progress]}
                      type="donut"
                      width={40}
                      height={40}
                    />
                    <span className="Amount">{learner.progress}%</span>
                  </div>
                </td>
                <td onClick={() => onSelectLearner(learner.id)}>
                  <span className="icon-eye"></span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
);

export default LearnersAnalyticsTable;
